import React, { useMemo, useState } from "react";
import {
  LuX,
  LuMonitor,
  LuSmartphone,
  LuExternalLink,
  LuClock,
  LuCalendar,
  LuShare2,
  LuChevronRight,
  LuCircleCheck,
  LuBookOpen,
} from "react-icons/lu";
import { getBlogMarketingUrl } from "../../common/urlUtils";

const BlogFullPagePreviewModal = ({ isOpen, onClose, blogData = {} }) => {
  const [deviceMode, setDeviceMode] = useState("desktop"); // desktop | mobile

  const {
    title = "Untitled Blog Post",
    slug = "",
    subTitle = "",
    category = "UPSC Preparation",
    readTime = "8 min read",
    tags = "",
    authorName = "Achiwon Editorial Team",
    authorRole = "UPSC Expert Faculty",
    image = "",
    description = "",
  } = blogData;

  // Parse Headings for dynamic Table of Contents (TOC) unconditionally
  const { parsedHtml, headings } = useMemo(() => {
    if (!description || !isOpen) return { parsedHtml: "", headings: [] };

    const headingList = [];
    let counter = 0;

    const updated = description.replace(
      /<(h[23])([^>]*)>(.*?)<\/\1>/gi,
      (match, tag, attrs, content) => {
        counter++;
        const cleanText = content.replace(/<[^>]+>/g, "").trim();
        const idMatch = attrs.match(/id=["']([^"']+)["']/i);
        const headingId = idMatch
          ? idMatch[1]
          : `preview-sec-${counter}-${cleanText.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`;

        headingList.push({
          id: headingId,
          text: cleanText,
          level: tag.toLowerCase() === "h2" ? 2 : 3,
        });

        if (idMatch) return match;
        return `<${tag}${attrs} id="${headingId}">${content}</${tag}>`;
      }
    );

    return { parsedHtml: updated, headings: headingList };
  }, [description, isOpen]);

  if (!isOpen) return null;

  const tagList = Array.isArray(tags)
    ? tags
    : typeof tags === "string"
    ? tags.split(",").map((t) => t.trim()).filter(Boolean)
    : [];

  const todayFormatted = new Date().toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  // Handle in-preview quiz interaction
  const handlePreviewQuizSubmit = () => {
    const quizEl = document.getElementById("preview-article-body");
    if (!quizEl) return;
    const questions = quizEl.querySelectorAll("[data-answer]");
    let score = 0;
    const total = questions.length;
    if (total === 0) return;

    questions.forEach((q) => {
      const selected = q.querySelector("input:checked");
      if (selected && selected.value === q.dataset.answer) {
        score++;
      }
    });

    const resultBox = quizEl.querySelector(".mcq-result");
    if (resultBox) {
      resultBox.textContent = `Your Score: ${score} / ${total} — ${
        score === total
          ? "Excellent! Perfect Score 🎉"
          : score >= Math.ceil(total * 0.6)
          ? "Good job! Keep practising 👍"
          : "Keep practising and try again!"
      }`;
      resultBox.removeAttribute("hidden");
      resultBox.style.display = "block";
    }
  };

  const liveUrl = getBlogMarketingUrl(category, slug || "preview");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-2 sm:p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-7xl h-[94vh] flex flex-col bg-[#F8FAFC] rounded-2xl shadow-2xl overflow-hidden border border-border">
        {/* Top Control Bar */}
        <div className="flex flex-wrap items-center justify-between px-5 py-3 bg-[#114598] text-white gap-3 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider bg-white/15 px-2.5 py-1 rounded-md text-amber-300">
              Live Preview
            </span>
            <span className="text-sm font-semibold truncate max-w-md hidden sm:inline text-white/90">
              {title}
            </span>
          </div>

          {/* Device Toggle */}
          <div className="flex items-center gap-2">
            <div className="bg-black/25 rounded-xl p-1 flex items-center border border-white/10">
              <button
                type="button"
                onClick={() => setDeviceMode("desktop")}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  deviceMode === "desktop"
                    ? "bg-white text-[#114598] shadow-xs"
                    : "text-white/80 hover:text-white"
                }`}
              >
                <LuMonitor className="text-sm" /> Desktop View
              </button>
              <button
                type="button"
                onClick={() => setDeviceMode("mobile")}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  deviceMode === "mobile"
                    ? "bg-white text-[#114598] shadow-xs"
                    : "text-white/80 hover:text-white"
                }`}
              >
                <LuSmartphone className="text-sm" /> Mobile View
              </button>
            </div>

            {slug && (
              <a
                href={liveUrl}
                target="_blank"
                rel="noreferrer"
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#F68122] hover:bg-amber-600 text-white rounded-lg text-xs font-bold transition-all shadow-xs"
              >
                <LuExternalLink /> Open on Landing Page
              </a>
            )}

            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer ml-1"
            >
              <LuX className="text-xl" />
            </button>
          </div>
        </div>

        {/* Scrollable Preview Screen Container */}
        <div
          tabIndex={0}
          className="flex-1 min-h-0 overflow-y-auto bg-slate-100 p-2 sm:p-6 flex justify-center items-start custom-scrollbar focus:outline-none"
          style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}
        >
          <div
            className={`transition-all duration-300 bg-white shadow-xl rounded-2xl border border-slate-200 shrink-0 mb-10 ${
              deviceMode === "desktop"
                ? "w-full max-w-5xl"
                : "w-[400px] border-8 border-slate-800 rounded-[36px] shadow-2xl my-4"
            }`}
          >
            {/* Mock Landing Page Header Simulation */}
            <div className="bg-white border-b border-slate-100 px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#114598] flex items-center justify-center text-white font-black text-sm">
                  BEF
                </div>
                <span className="font-extrabold text-[#114598] tracking-tight">
                  Achiwon
                </span>
              </div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest hidden sm:inline">
                Article Page View
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="px-6 sm:px-10 py-4 bg-slate-50 border-b border-slate-100 text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
              <span>Home</span>
              <LuChevronRight className="text-slate-400" />
              <span>Blog</span>
              <LuChevronRight className="text-slate-400" />
              <span className="font-semibold text-[#114598]">{category}</span>
              <LuChevronRight className="text-slate-400" />
              <span className="truncate max-w-[200px]">{title}</span>
            </div>

            {/* Article Main Wrapper */}
            <div className={deviceMode === "mobile" ? "px-4 py-5" : "px-6 sm:px-12 py-8"}>
              {/* Category & Read Time Badges */}
              <div className="flex items-center gap-2.5 flex-wrap mb-4">
                <span className="px-3 py-1 bg-blue-50 text-[#114598] text-xs font-extrabold rounded-full uppercase tracking-wider border border-blue-100">
                  {category}
                </span>
                <span className="flex items-center gap-1 text-xs text-slate-500 font-semibold">
                  <LuClock className="text-amber-500" /> {readTime}
                </span>
                <span className="flex items-center gap-1 text-xs text-slate-500 font-semibold">
                  <LuCalendar className="text-slate-400" /> {todayFormatted}
                </span>
              </div>

              {/* Title & SubTitle */}
              <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
                {title}
              </h1>
              {subTitle && (
                <p className="mt-3 text-base sm:text-lg text-slate-600 leading-relaxed">
                  {subTitle}
                </p>
              )}

              {/* Author & Share Row */}
              <div className="mt-6 pt-5 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <img
                    src="https://randomuser.me/api/portraits/women/44.jpg"
                    alt={authorName}
                    className="w-11 h-11 rounded-full object-cover border-2 border-primary"
                  />
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">{authorName}</h4>
                    <p className="text-xs text-slate-500">{authorRole}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                  <LuShare2 className="text-base text-slate-400" /> Share Article
                </div>
              </div>

              {/* Featured Image */}
              {image && (
                <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200 shadow-md">
                  <img
                    src={image}
                    alt={title}
                    className="w-full aspect-[16/8] object-cover"
                  />
                </div>
              )}

              {/* Two Column Layout (Desktop) or Clean Stacked Layout (Mobile) */}
              <div
                className={`mt-8 ${
                  deviceMode === "mobile"
                    ? "space-y-6"
                    : "grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
                }`}
              >
                {/* Table of Contents */}
                {headings.length > 0 && (
                  deviceMode === "mobile" ? (
                    /* Mobile Collapsible TOC */
                    <details className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <summary className="cursor-pointer list-none font-bold text-xs text-[#114598] flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <LuBookOpen className="text-base text-primary" />
                          <span>Table of Contents ({headings.length} sections)</span>
                        </span>
                        <span className="text-[10px] font-semibold text-slate-500 bg-white px-2 py-0.5 rounded-md border border-slate-200">
                          Tap to View ▾
                        </span>
                      </summary>
                      <nav className="mt-3 pt-3 border-t border-slate-200 space-y-1.5 text-xs max-h-52 overflow-y-auto custom-scrollbar">
                        {headings.map((h, i) => (
                          <a
                            key={i}
                            href={`#${h.id}`}
                            className={`block py-1 transition-colors hover:text-[#114598] ${
                              h.level === 3 ? "pl-3 text-slate-500 text-[11px]" : "font-bold text-slate-700"
                            }`}
                          >
                            {h.text}
                          </a>
                        ))}
                      </nav>
                    </details>
                  ) : (
                    /* Desktop Sticky Sidebar TOC */
                    <aside className="lg:col-span-4 lg:sticky lg:top-4 bg-slate-50 p-5 rounded-2xl border border-slate-200">
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200 text-[#114598]">
                        <LuBookOpen className="text-lg" />
                        <h3 className="text-xs font-extrabold uppercase tracking-wider">
                          Table of Contents
                        </h3>
                      </div>
                      <nav className="space-y-1.5 text-xs max-h-[50vh] overflow-y-auto custom-scrollbar">
                        {headings.map((h, i) => (
                          <a
                            key={i}
                            href={`#${h.id}`}
                            className={`block py-1 transition-colors hover:text-[#114598] ${
                              h.level === 3 ? "pl-3 text-slate-500 text-[11px]" : "font-bold text-slate-700"
                            }`}
                          >
                            {h.text}
                          </a>
                        ))}
                      </nav>
                    </aside>
                  )
                )}

                {/* Right / Main Content */}
                <main
                  className={
                    deviceMode === "mobile"
                      ? "w-full"
                      : headings.length > 0
                      ? "lg:col-span-8"
                      : "lg:col-span-12"
                  }
                >
                  <div
                    id="preview-article-body"
                    className="prose prose-slate max-w-none text-slate-800 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html:
                        parsedHtml ||
                        `<p class="text-slate-400 italic">No content has been added to this blog yet. Use Visual Editor or Table/Quiz builders to write content.</p>`,
                    }}
                    onClick={(e) => {
                      if (e.target && e.target.tagName === "BUTTON") {
                        if (
                          e.target.textContent &&
                          e.target.textContent.toLowerCase().includes("submit")
                        ) {
                          e.preventDefault();
                          handlePreviewQuizSubmit();
                        }
                      }
                    }}
                  />

                  {/* Tags Badges */}
                  {tagList.length > 0 && (
                    <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Tags:
                      </span>
                      {tagList.map((tag, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Related Articles Box Simulation */}
                  <div className="mt-14 p-6 bg-blue-50/60 rounded-2xl border border-blue-100">
                    <h3 className="font-bold text-[#114598] text-base mb-2">
                      Related Preparation Articles
                    </h3>
                    <p className="text-xs text-slate-600 mb-4">
                      Explore more expert guides curated by Achiwon.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div className="p-3 bg-white rounded-xl border border-blue-100 shadow-2xs font-semibold text-slate-800">
                        → How Can Achiwon Help You Improve Your UPSC Preparation?
                      </div>
                      <div className="p-3 bg-white rounded-xl border border-blue-100 shadow-2xs font-semibold text-slate-800">
                        → CNAP (Calling Name Presentation) in India: Complete Overview
                      </div>
                    </div>
                  </div>
                </main>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Bottom Bar */}
        <div className="px-6 py-3.5 bg-white border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <LuCircleCheck className="text-emerald-500 text-base" />
            <span>
              This preview matches the exact responsive layout of Achiwon Landing Page.
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-[#114598] hover:bg-[#F68122] text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogFullPagePreviewModal;
